/**
 * Pestaña AJUSTES de la organización (org_settings): país/tz/monedas/escala/
 * umbral de alertas + marca + overrides de features. Escritura local-first →
 * write-path (authz owner/org_admin). Las etiquetas locales salen del country
 * pack.
 *
 * AUTONOMY:
 *  · La escala de calificación usa las claves REALES del registry compartido
 *    (antes había un literal local con claves inexistentes que caían en
 *    silencio al default).
 *  · La tasa de cambio se gestiona SOLO en Finanzas → Tasas (historial diario);
 *    aquí se muestra la última cacheada, sin segundo write-path.
 *  · Branding (nombre público, usado p.ej. en recibos) y overrides de features
 *    org/curso (registry-driven, semántica course→org→platform).
 */
import { msg } from "@lingui/core/macro";
import { createEffect, createSignal, For, Show } from "solid-js";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TextField } from "@/components/ui/text-field";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/components/ui/toast";
import { InfoHint } from "@/components/ui/info-hint";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@/lib/u-router";
import {
  dependentsOf,
  featureGraph,
  getCountryPack,
  listCountryPacks,
  modelShapeFromRow,
  modelStrategy,
  orgConfigurableFeatures,
  parseFeatureOverrides,
  parseRequireClause,
  resolveFeatureEffective,
  saveOrgSettingsMutation,
  serializeFeatureOverrides,
  validateOrgOverridesChange,
} from "@space/shared";
// FLAGS: confirmación con implicaciones + validación del grafo antes de aplicar.
import {
  FeatureToggleDialog,
  type FeatureToggleRequest,
} from "@/features/flags/feature-toggle-dialog";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";
import { useAcademyWrite, useGradingModels, useOrgSettings } from "../hooks/use-academy-data";

interface BrandingShape {
  publicName?: string;
  tagline?: string;
}

function parseBranding(raw: string | null | undefined): BrandingShape {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as BrandingShape)
      : {};
  } catch {
    return {};
  }
}

/** Estado del select de override por feature. */
type OverrideChoice = "default" | "on" | "off";

function toOverrideChoice(v: string): OverrideChoice {
  return v === "on" || v === "off" ? v : "default";
}

export function OrgSettingsTab(props: { orgId: string }) {
  const { t } = useI18n();
  const settings = useOrgSettings(() => props.orgId);
  const { drizzle } = useAcademyWrite();
  // Grupo E: la escala por defecto de la org se elige entre sus MODELOS (sync).
  const gradingModels = useGradingModels(() => props.orgId);
  const modelSummary = (m: {
    kind: string | null;
    max_value: number | null;
    passing_threshold: number | null;
    decimals: number | null;
    letter_bands: unknown;
    display_suffix: string | null;
    system_key: string | null;
  }): string => {
    const shape = modelShapeFromRow({
      id: "x",
      kind: m.kind ?? "points",
      max_value: m.max_value ?? 0,
      passing_threshold: m.passing_threshold ?? 0,
      decimals: m.decimals ?? 0,
      letter_bands: m.letter_bands,
      display_suffix: m.display_suffix,
      system_key: m.system_key,
    });
    const strat = modelStrategy(shape);
    return `${strat.displayValue(shape.passingThreshold * shape.maxValue)} / ${strat.displayValue(shape.maxValue)}`;
  };

  const [country, setCountry] = createSignal("VE");
  const [timezone, setTimezone] = createSignal("America/Caracas");
  const [locale, setLocale] = createSignal("es");
  const [currencyPrimary, setCurrencyPrimary] = createSignal("USD");
  const [currencySecondary, setCurrencySecondary] = createSignal("VES");
  const [gradingModelId, setGradingModelId] = createSignal("");
  const [threshold, setThreshold] = createSignal("3");
  const [publicName, setPublicName] = createSignal("");
  const [tagline, setTagline] = createSignal("");
  const [overrides, setOverrides] = createSignal<Record<string, boolean>>({});
  const [saving, setSaving] = createSignal(false);
  // FLAGS: cambio de override pendiente de confirmación (diálogo).
  const [pendingChange, setPendingChange] = createSignal<
    (FeatureToggleRequest & { choice: OverrideChoice }) | null
  >(null);
  const platformFlags = usePlatformFlags();
  const graph = featureGraph();

  // Hidrata el formulario desde la fila sincronizada (una vez cargada).
  createEffect(() => {
    const s = settings.data;
    if (!s) {
      return;
    }
    setCountry(s.country ?? "VE");
    setTimezone(s.timezone ?? "America/Caracas");
    setLocale(s.locale ?? "es");
    setCurrencyPrimary(s.currency_primary ?? "USD");
    setCurrencySecondary(s.currency_secondary ?? "VES");
    setGradingModelId(s.grading_model_id ?? "");
    setThreshold(String(s.attendance_alert_threshold ?? 3));
    const branding = parseBranding(s.branding);
    setPublicName(branding.publicName ?? "");
    setTagline(branding.tagline ?? "");
    setOverrides({ ...parseFeatureOverrides(s.features) });
  });

  const pack = () => getCountryPack(country());
  const configurableFeatures = orgConfigurableFeatures();

  const overrideChoice = (key: string): OverrideChoice => {
    const value = overrides()[key];
    return value === undefined ? "default" : value ? "on" : "off";
  };

  const applyOverrideChoice = (key: string, choice: OverrideChoice) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (choice === "default") {
        delete next[key];
      } else {
        next[key] = choice === "on";
      }
      return next;
    });
  };

  /**
   * FLAGS: todo toggle pasa por el diálogo de confirmación con implicaciones
   * + efectos de grafo. Las violaciones DURAS (mismo delta que valida el
   * server en el write-path) bloquean Confirmar.
   */
  const setOverrideChoice = (key: string, choice: OverrideChoice) => {
    if (choice === overrideChoice(key)) {
      return;
    }
    const prev = overrides();
    const next = { ...prev };
    if (choice === "default") {
      delete next[key];
    } else {
      next[key] = choice === "on";
    }
    const violations = validateOrgOverridesChange({
      platform: platformFlags(),
      oldOrg: prev,
      newOrg: next,
    });
    const feature = configurableFeatures.find((f) => f.key === key);
    const nextEnabled = resolveFeatureEffective(key, {
      platformMap: platformFlags(),
      org: next,
    }).enabled;
    const softAffected = nextEnabled
      ? []
      : dependentsOf(key, graph)
          .filter((n) =>
            n.requires.some((clause) =>
              parseRequireClause(clause).some((a) => a.key === key && a.soft),
            ),
          )
          .map((n) => n.name);
    setPendingChange({
      choice,
      key,
      name: feature?.name ?? key,
      nextEnabled,
      status: feature?.status,
      since: feature?.since,
      implications: feature?.implications,
      violations,
      softAffected,
    });
  };

  const handleSave = async (e: Event) => {
    e.preventDefault();
    const db = drizzle();
    if (!db) {
      return;
    }
    setSaving(true);
    try {
      const branding: BrandingShape = {};
      if (publicName().trim()) {
        branding.publicName = publicName().trim();
      }
      if (tagline().trim()) {
        branding.tagline = tagline().trim();
      }
      await saveOrgSettingsMutation(
        db,
        props.orgId,
        {
          country: country(),
          timezone: timezone(),
          locale: locale(),
          currency_primary: currencyPrimary(),
          currency_secondary: currencySecondary() || null,
          grading_model_id: gradingModelId() || null,
          attendance_alert_threshold: Number(threshold()) || 3,
          features: serializeFeatureOverrides(overrides()),
          branding: JSON.stringify(branding),
        },
        Boolean(settings.data),
      );
      showToast(t(msg`Settings saved`), "success");
    } catch (err) {
      console.error("Error saving org settings:", err);
      showToast(t(msg`Error saving settings`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle class="text-lg font-bold">{t(msg`Organization settings`)}</CardTitle>
        {/* AUTONOMY · Layer 2: propósito visible, no solo campos. */}
        <p class="text-sm text-muted-foreground">
          {t(
            msg`These settings apply to your whole academy: local conventions, currencies, default grading scale, absence alerts, public branding and feature overrides. Courses and cohorts can refine some of them.`,
          )}
        </p>
      </CardHeader>
      <CardContent>
        <Show when={!settings.isLoading} fallback={<Skeleton class="h-64 w-full rounded-lg" />}>
          <form onSubmit={handleSave} class="space-y-5" data-testid="org-settings-form">
            <div class="grid gap-4 sm:grid-cols-2">
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Country`)}{" "}
                  <InfoHint>
                    {t(
                      msg`The country pack sets local conventions: ID document label, tax id, phone prefix, date format and currencies. It changes labels across the app.`,
                    )}
                  </InfoHint>
                </span>
                <Select
                  aria-label={t(msg`Country`)}
                  value={country()}
                  onChange={setCountry}
                  options={listCountryPacks().map((p) => ({
                    value: p.code.toUpperCase(),
                    label: p.name,
                  }))}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Time zone`)}</span>
                <TextField value={timezone()} onInput={(e) => setTimezone(e.currentTarget.value)} />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Locale`)}</span>
                <TextField value={locale()} onInput={(e) => setLocale(e.currentTarget.value)} />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Grading model`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Default grading model for final grades (courses and cohorts can override it). Manage models in the Grading models tab. Changing it does not rewrite already-sealed grades.`,
                    )}
                  </InfoHint>
                </span>
                <Select
                  aria-label={t(msg`Grading model`)}
                  value={gradingModelId()}
                  onChange={setGradingModelId}
                  options={(gradingModels.data ?? []).map((m) => ({
                    value: m.id,
                    label: `${m.name ?? ""} (${modelSummary(m)})`,
                  }))}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Primary currency`)}</span>
                <TextField
                  value={currencyPrimary()}
                  onInput={(e) => setCurrencyPrimary(e.currentTarget.value.toUpperCase())}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Secondary currency`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Amounts are stored in the primary currency and displayed in both, converted with the exchange rate of the day.`,
                    )}
                  </InfoHint>
                </span>
                <TextField
                  value={currencySecondary()}
                  onInput={(e) => setCurrencySecondary(e.currentTarget.value.toUpperCase())}
                />
              </label>
              <label class="space-y-1.5">
                <span class="text-sm font-medium">
                  {t(msg`Absence alert threshold`)}{" "}
                  <InfoHint>
                    {t(
                      msg`Consecutive absences that open a risk alert for a student, visible to admins in the cohort's attendance tab.`,
                    )}
                  </InfoHint>
                </span>
                <TextField
                  type="number"
                  inputmode="numeric"
                  value={threshold()}
                  data-testid="org-threshold-input"
                  onInput={(e) => setThreshold(e.currentTarget.value)}
                />
              </label>
              {/* AUTONOMY: la tasa tiene UNA sola vía de escritura (Finanzas →
                  Tasas, con historial); aquí solo se informa la última. */}
              <div class="space-y-1.5">
                <span class="text-sm font-medium">{t(msg`Exchange rate`)}</span>
                <p class="rounded-md border border-border/60 bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  <Show
                    when={typeof settings.data?.exchange_rate === "number"}
                    fallback={t(msg`No rate registered yet.`)}
                  >
                    {String(settings.data?.exchange_rate)} {currencySecondary()}/{currencyPrimary()}
                  </Show>
                </p>
                <Link
                  to="/finance/rates"
                  class="text-xs font-semibold text-primary hover:underline"
                >
                  {t(msg`Manage the daily rate in Finance → Rates`)}
                </Link>
              </div>
            </div>

            {/* ── AUTONOMY: branding de la academia ── */}
            <div class="space-y-3 rounded-xl border border-border/60 p-4">
              <h3 class="text-sm font-bold">{t(msg`Branding`)}</h3>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Public name and tagline of your academy, used on public pages and printable receipts. Leave empty to use the organization name.`,
                )}
              </p>
              <div class="grid gap-4 sm:grid-cols-2">
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Public name`)}</span>
                  <TextField
                    value={publicName()}
                    data-testid="org-branding-public-name"
                    onInput={(e) => setPublicName(e.currentTarget.value)}
                  />
                </label>
                <label class="space-y-1.5">
                  <span class="text-sm font-medium">{t(msg`Tagline`)}</span>
                  <TextField value={tagline()} onInput={(e) => setTagline(e.currentTarget.value)} />
                </label>
              </div>
            </div>

            {/* ── AUTONOMY: overrides de features (registry-driven) ── */}
            <div class="space-y-3 rounded-xl border border-border/60 p-4">
              <h3 class="text-sm font-bold">{t(msg`Feature overrides`)}</h3>
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Enable or disable platform features for this academy. "Default" inherits the platform state; courses can refine course-scoped features further.`,
                )}
              </p>
              <div class="space-y-2" data-testid="org-feature-overrides">
                <For each={configurableFeatures}>
                  {(feature) => (
                    <div
                      class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2"
                      data-testid={`org-feature-row-${feature.key}`}
                    >
                      <div class="min-w-0">
                        <p class="truncate text-sm font-medium">{feature.name}</p>
                        <p
                          class="truncate text-xs text-muted-foreground"
                          title={feature.description}
                        >
                          {feature.description}
                        </p>
                      </div>
                      <Select
                        aria-label={feature.name}
                        value={overrideChoice(feature.key)}
                        onChange={(v) => {
                          setOverrideChoice(feature.key, toOverrideChoice(v));
                        }}
                        options={[
                          { value: "default", label: t(msg`Default`) },
                          { value: "on", label: t(msg`Enabled`) },
                          { value: "off", label: t(msg`Disabled`) },
                        ]}
                      />
                    </div>
                  )}
                </For>
              </div>
            </div>

            <div class="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
              {(() => {
                const { idLabel } = pack();
                const { taxIdLabel } = pack();
                const { phonePrefix } = pack();
                const { dateFormat } = pack();
                return t(msg`${idLabel} · ${taxIdLabel} · ${phonePrefix} · ${dateFormat}`);
              })()}
            </div>
            <Button type="submit" disabled={saving()} data-testid="org-settings-save">
              <Show when={saving()} fallback={t(msg`Save settings`)}>
                {t(msg`Saving...`)}
              </Show>
            </Button>
          </form>
        </Show>
      </CardContent>

      {/* FLAGS: confirmación de cada toggle con implicaciones + grafo. */}
      <FeatureToggleDialog
        request={pendingChange()}
        graph={graph}
        onConfirm={() => {
          const change = pendingChange();
          setPendingChange(null);
          if (change) {
            applyOverrideChoice(change.key, change.choice);
          }
        }}
        onCancel={() => setPendingChange(null)}
      />
    </Card>
  );
}
